import { Divider, Typography } from '@mui/material';
import {
  Service,
  StaffAppointmentAvailability,
} from 'common/infrastructure/modules/appointment/interfaces/AvailabilityTypes';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { selectLocation } from 'infrastructure/redux/slices/bookingWizard.selector';
import { setLessonType, setReservationDate } from 'infrastructure/redux/slices/bookingWizard.slice';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
import useEventTracking, {
  EventType,
} from 'infrastructure/targets/web/modules/common/hooks/useEventTracking';
import FirstTimeLessonCard from 'infrastructure/targets/web/modules/bookingWizard/Steps/SelectInstructorStep/components/FirstTimeLessonCard';
import InstructorsFilterSelect from 'infrastructure/targets/web/modules/bookingWizard/Steps/SelectInstructorStep/components/InstructorsFilterSelect';
import InstructorsList from 'infrastructure/targets/web/modules/bookingWizard/Steps/SelectInstructorStep/components/InstructorsList';

import { StyledCardsContainer, StyledContentContainer, StyledTitleContainer } from './style';

interface ILessonByCoach {
  instructorsList: StaffAppointmentAvailability[] | undefined;
  getInstructors: any;
  email: string | null;
  instructorsLoading: boolean;
  preselectInstructor: (instructorSlug: string | null) => number | undefined;
  preselectLocation: (locationSlug: string) => void;
  preselectReservationDate: (reservationDate: string) => void;
  nextStep: (selectedInstructorId: number | null, isFirstTimeLesson?: boolean) => void;
}

const LessonByCoach: FC<ILessonByCoach> = ({
  instructorsList,
  getInstructors,
  email,
  instructorsLoading,
  preselectInstructor,
  preselectLocation,
  preselectReservationDate,
  nextStep,
}) => {
  const dispatch = useAppDispatch();
  const today = useRef(new Date());
  const selectedLocationId = useAppSelector(selectLocation);
  const { search } = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const locationSlug = query?.get('location');
  const staffName = query?.get('staff');
  const reservationDate = query?.get('reservationDate') ?? `${today.current}`;

  const { sendEvent } = useEventTracking();

  const [instructorsToShow, setInstructorsToShow] = useState(instructorsList);
  const [firstTimeLessonService, setFirstTimeLessonService] = useState<Service | null>(null);

  useEffect(() => {
    if (locationSlug && staffName && selectedLocationId) {
      getInstructors({
        locationId: selectedLocationId,
        email,
        checkAvailability: false,
      });
    } else if (selectedLocationId) {
      getInstructors({
        locationId: selectedLocationId,
        email,
        checkAvailability: true,
      });
    }
  }, [locationSlug, staffName, selectedLocationId, email]);

  useEffect(() => {
    if (locationSlug && staffName && instructorsList && instructorsList.length > 0) {
      preselectReservationDate(reservationDate);
      preselectLocation(locationSlug);
      const preselectedInstructor = preselectInstructor(staffName);
      if (preselectedInstructor) {
        nextStep(preselectedInstructor);
      }
    }
  }, [locationSlug, staffName, instructorsList]);

  const handleCardClick = (value: number) => {
    nextStep(value);
    sendEvent(EventType.SelectExperience, {
      experience: 'lesson',
      instructor: instructorsList?.find((i) => i.staffId === value),
      utm: localStorage.getItem('utm') ? JSON.parse(localStorage.getItem('utm') as string) : null,
    });
  };

  const handleSelectFirstTimeLesson = () => {
    if (firstTimeLessonService) {
      dispatch(setLessonType({ lessonType: firstTimeLessonService.sessionTypeId }));
    }
    nextStep(null, true);
    sendEvent(EventType.SelectExperience, {
      experience: 'lesson',
      instructor: 'Swing Evaluation',
      utm: localStorage.getItem('utm') ? JSON.parse(localStorage.getItem('utm') as string) : null,
    });
  };

  const handleInstructorsFilter = (instructorTypes: string[]) => {
    const filteredInstructors = filterNonLessonInstructors()?.filter((instructor) =>
      instructorTypes.includes(instructor.staffLevel),
    );
    setInstructorsToShow(filteredInstructors);
  };

  const getFirstTimeLessonContent = (): string =>
    `The Swing Evaluation is a 60-minute session designed to discuss your golf history and any frustrations you are currently having with your game. ` +
    `Then, together, we'll work to establish measurable goals. We will introduce you to the TrackMan technology and evaluate your swing based on ` +
    `seeing you hit multiple different shots, before identifying the root cause of your inconsistencies. ` +
    `Most importantly, we will develop a custom improvement plan to get you on the path towards achieving your golfing dreams!`;

  const filterNonLessonInstructors = () =>
    // filter out instructors who don't teach at least one lesson type
    instructorsList?.filter(
      (instructor) =>
        instructor.services.some((service) =>
          service.title.toLocaleLowerCase().includes('lesson'),
        ) ||
        instructor.services.some((service) => service.title.toLocaleLowerCase().includes('swing')),
    );
  useEffect(() => {
    setInstructorsToShow(filterNonLessonInstructors());
    if (instructorsList?.length) {
      const firstTimeLessonType = instructorsList
        .flatMap((instructor) => instructor.services)
        .find((service) => service.title.toLocaleLowerCase().includes('swing eval'));
      if (firstTimeLessonType) {
        setFirstTimeLessonService(firstTimeLessonType as Service);
      }
    }
  }, [instructorsList]);

  useEffect(() => {
    dispatch(setReservationDate({ reservationDate: `${today.current}` }));
  }, []);
  return (
    <StyledContentContainer>
      <FirstTimeLessonCard
        instructorsLoading={instructorsLoading}
        onCardClick={handleSelectFirstTimeLesson}
        content={getFirstTimeLessonContent()}
      />
      <Divider />
      <StyledTitleContainer sx={{ marginTop: '48px' }}>
        <Typography variant="h3">Browse & Book With Five Iron Coaches</Typography>
        <InstructorsFilterSelect onChange={handleInstructorsFilter} />
      </StyledTitleContainer>
      <StyledCardsContainer>
        <InstructorsList
          loading={instructorsLoading}
          onCardClick={handleCardClick}
          instructorsList={instructorsToShow}
        />
      </StyledCardsContainer>
    </StyledContentContainer>
  );
};

export default LessonByCoach;
